import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BdsLogo from "@/components/BdsLogo";
import usePageTitle from "@/hooks/usePageTitle";
import { fx } from "@/components/ActionFx";

export default function AdminLogin() {
  usePageTitle("Administrator Login");
  const { login, user } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(username, password);
    setBusy(false);
    if (res.ok) {
      fx.saved("Login successful");
      navigate("/admin");
    } else setError(res.error);
  };

  return (
    <div className="relative flex min-h-screen items-center bg-[#F8FAFC] px-6 py-12">
      <div className="absolute -left-32 top-10 h-80 w-80 rounded-full bg-indigo-200/40 blur-3xl" />
      <div className="relative mx-auto w-full max-w-md">
        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="mb-6 flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-indigo-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="bds-card p-7"
        >
          <span className="inline-flex rounded-xl bg-indigo-600 p-3 text-white shadow-lg shadow-indigo-600/25">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div className="mt-5">
            <BdsLogo size={44} />
          </div>
          <h1 className="mt-5 font-outfit text-2xl font-semibold text-slate-900">Administrator Login</h1>
          <p className="mt-1 text-sm text-slate-500">Bharat Digital Seva secure admin area.</p>

          <label className="mt-6 block text-xs font-semibold uppercase tracking-wide text-slate-500">Username</label>
          <input
            data-testid="login-username-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />

          <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-2 h-12 w-full rounded-xl border border-slate-200 px-4 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />

          {error && (
            <p data-testid="login-error" className="mt-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-btn"
            disabled={busy}
            className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Login
          </button>
          <p className="mt-4 text-center text-xs text-slate-400">
            Sessions expire automatically. Failed attempts are rate limited.
          </p>
        </motion.form>
      </div>
    </div>
  );
}
